import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, X, Globe, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const norm = (s) => (s || "").toLowerCase().replace(/[\s\-_./]/g, "");

export function PageSearch() {
  const { pathname } = useLocation();
  const [q, setQ] = useState("");
  const [stat, setStat] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => { setQ(""); }, [pathname]);
  useEffect(() => {
    const apply = () => {
      const rows = Array.from(document.querySelectorAll("main table tbody tr")).filter((tr) => !tr.closest("[data-nosearch]"));
      const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
      let shown = 0;
      const ranges = [];
      rows.forEach((tr) => {
        const txt = tr.innerText || tr.textContent || "";
        const ok = !terms.length || terms.every((t) => txt.toLowerCase().includes(t) || norm(txt).includes(norm(t)));
        tr.style.display = ok ? "" : "none";
        if (ok) shown++;
        if (ok && terms.length && window.CSS?.highlights) {
          const walker = document.createTreeWalker(tr, NodeFilter.SHOW_TEXT);
          let node;
          while ((node = walker.nextNode())) {
            const low = node.textContent.toLowerCase();
            terms.forEach((t) => { let i = low.indexOf(t); while (i >= 0) { const r = new Range(); r.setStart(node, i); r.setEnd(node, i + t.length); ranges.push(r); i = low.indexOf(t, i + t.length); } });
          }
        }
      });
      if (window.CSS?.highlights) { if (ranges.length) window.CSS.highlights.set("pp-search", new window.Highlight(...ranges)); else window.CSS.highlights.delete("pp-search"); }
      setStat(terms.length && rows.length ? { shown, total: rows.length } : null);
    };
    apply();
    const obs = new MutationObserver((muts) => { if (q && muts.some((m) => m.type === "childList")) apply(); });
    const main = document.querySelector("main");
    if (main) obs.observe(main, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [q, pathname]);
  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  if (pathname === "/login") return null;
  return (
    <div className="relative" data-testid="page-search">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[16rem] max-w-xl">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input ref={ref} className="pp-input h-9 w-full pl-9 pr-8 text-sm" placeholder="Find on this page — SKU / Model / Part code / Customer  (Ctrl+K)" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} data-testid="page-search-input" />
          {q && <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" onClick={() => setQ("")} data-testid="page-search-clear"><X size={14} /></button>}
        </div>
        {stat && <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${stat.shown ? "bg-blue-50 text-blue-800" : "bg-rose-100 text-rose-800"}`} data-testid="page-search-count">{stat.shown} of {stat.total} rows match</span>}
      </div>
      <Everywhere q={q} open={open} pathname={pathname} />
    </div>
  );
}

function Everywhere({ q, open, pathname }) {
  const nav = useNavigate();
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(async () => { try { setRes((await api.get("/search", { params: { q: q.trim() } })).data); } catch { setRes(null); } }, 350);
    return () => clearTimeout(t);
  }, [q]);
  if (!open || !res) return null;
  const groups = res.groups.filter((g) => g.route.split("?")[0] !== pathname);
  return (
    <div className="absolute z-40 mt-1 w-full max-w-xl rounded-lg border border-slate-200 bg-white shadow-xl p-2 text-sm" data-testid="search-everywhere">
      <div className="pp-label px-2 py-1 flex items-center gap-1"><Globe size={12} /> Also found in other modules</div>
      {!groups.length && <div className="px-2 py-2 text-xs text-slate-400" data-testid="search-everywhere-empty">No matches for "{res.q}" in other modules</div>}
      {groups.map((g) => <button key={g.route} type="button" className="w-full text-left rounded-md px-2 py-1.5 hover:bg-blue-50 flex items-start gap-2" onMouseDown={(e) => e.preventDefault()} onClick={() => nav(g.route)} data-testid={`search-everywhere-${g.module}`}>
        <span className="mt-0.5 shrink-0 rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold text-white">{g.count}</span>
        <span className="min-w-0"><span className="font-semibold">{g.title}</span><span className="ml-1 text-slate-400"><ArrowRight size={10} className="inline" /></span><div className="truncate text-xs text-slate-500">{g.items.map((i) => i.label).join(" · ")}</div></span></button>)}
    </div>
  );
}
